import * as Api from '@ViewModels';
import { useQuery } from '@tanstack/react-query';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { reactQueryClient } from '../../../web/ReactQueryProvider';

const BASE_QUERY_KEY = 'automationTemplate/<id>/eligibleContacts';

/**
 * Response structure for eligible contacts query.
 *
 * Contains both the paginated list of contacts and the total count of all
 * eligible contacts (which may exceed the returned values length).
 */
export interface IEligibleContactsResponse {
	/** Array of contacts that meet the automation's eligibility criteria */
	values: Api.IContact[];
	/** Total number of eligible contacts (may be larger than values.length) */
	totalCount: number;
}

/**
 * Fetches contacts who are eligible to enter an automation.
 *
 * This query returns contacts that meet the automation's trigger criteria and
 * aren't already in the automation. Useful for previewing who would be affected
 * by starting an automation or for testing automation flows.
 *
 * Eligibility is determined by:
 * - Automation trigger conditions (tags, segments, custom filters)
 * - Contact not already active in this automation
 * - Contact meets any additional entry requirements
 *
 * Use cases:
 * - Showing a preview before starting an automation
 * - Selecting test contacts for flow validation
 * - Estimating impact of an automation change
 * - Debugging why specific contacts aren't entering
 *
 * Draft vs Published:
 * - useDraft=true: Uses draft trigger conditions (for testing before publish)
 * - useDraft=false: Uses published trigger conditions (current active criteria)
 *
 * @param options - Query configuration
 * @param options.templateId - Automation template ID
 * @param options.useDraft - Whether to use draft trigger or published trigger (default: false)
 * @param options.limit - Maximum number of contacts to return (default: 5)
 * @param options.enabled - Whether to enable the query (default: true)
 * @param options.impersonationContext - Optional impersonation context for admin users
 * @returns React Query result with eligible contacts and total count
 *
 * @example
 * ```typescript
 * const { data, isLoading } = useAutomationTemplateEligibleContactsQuery({
 *   templateId: 'template-123',
 *   useDraft: true, // Preview who would enter with current draft trigger
 *   limit: 10
 * });
 *
 * // Show eligible contacts preview:
 * <div>
 *   <p>{data?.totalCount} contacts eligible</p>
 *   <ContactList contacts={data?.values || []} />
 *   {data && data.totalCount > data.values.length && (
 *     <p>+ {data.totalCount - data.values.length} more</p>
 *   )}
 * </div>
 * ```
 *
 * @example
 * ```typescript
 * // Check if any contacts would enter:
 * const { data } = useAutomationTemplateEligibleContactsQuery({
 *   templateId,
 *   limit: 1 // Only need to know if at least one exists
 * });
 *
 * const hasEligibleContacts = (data?.totalCount ?? 0) > 0;
 * ```
 */
export function useAutomationTemplateEligibleContactsQuery({
	templateId,
	useDraft = false,
	limit = 5,
	enabled = true,
	impersonationContext,
}: {
	templateId: string;
	useDraft?: boolean;
	limit?: number;
	enabled?: boolean;
	impersonationContext?: Api.IImpersonationContext;
}) {
	const userSession = useUserSession();

	return useQuery({
		enabled: enabled && !!templateId,
		queryFn: () => {
			return userSession.webServiceHelper.callAsync<IEligibleContactsResponse>(
				Api.ImpersonationBroker.composeApiUrl({
					urlPath: `automationtemplate/${encodeURIComponent(templateId)}/eligiblecontacts`,
					queryParams: { useDraft, limit },
					impersonationContext,
				}),
				'GET'
			);
		},
		queryKey: [
			BASE_QUERY_KEY,
			templateId,
			useDraft,
			limit,
			impersonationContext?.account?.id,
			impersonationContext?.user?.id,
		],
		// Eligible contacts may change frequently as contact data updates (e.g., segments, filters).
		// Use a shorter staleTime of 1 minute to keep preview data relatively fresh.
		staleTime: 1 * 60 * 1000, // 1 minute
		// Keep in cache for 3 minutes after unmounting for quick re-previews.
		gcTime: 3 * 60 * 1000, // 3 minutes
		// Don't refetch on window focus - eligibility is based on template criteria which user controls.
		refetchOnWindowFocus: false,
	});
}

/**
 * Invalidates cached eligible contacts queries for a specific template.
 *
 * Call this after actions that change who would be eligible to enter the automation,
 * such as modifying trigger conditions or segment definitions.
 *
 * Common use cases:
 * - After changing automation trigger conditions
 * - After publishing draft trigger changes
 * - After modifying segments referenced in the trigger
 * - After bulk contact updates that affect eligibility
 *
 * @param templateId - Automation template ID whose eligible contacts should be refetched
 *
 * @example
 * ```typescript
 * const updateTriggerMutation = useMutation({
 *   mutationFn: updateAutomationTrigger,
 *   onSuccess: () => {
 *     invalidateAutomationTemplateEligibleContactsQueries(templateId);
 *     showToast('Trigger updated - preview refreshed');
 *   }
 * });
 * ```
 */
export function invalidateAutomationTemplateEligibleContactsQueries(templateId: string) {
	reactQueryClient.invalidateQueries({
		queryKey: [BASE_QUERY_KEY, templateId],
	});
}
